/**
 * Transaction formatter utilities
 *
 * Provides human-readable labels, descriptions, and error messages
 * for blockchain transactions and events
 */

export const METHOD_DISPLAY_NAMES = {
  // Balances
  "balances.transfer": "Token Transfer",
  "balances.transferKeepAlive": "Safe Transfer",
  "balances.transferAll": "Transfer All",
  "balances.forceTransfer": "Force Transfer",
  "balances.setBalance": "Set Balance",

  // System
  "timestamp.set": "Set Block Time",
  "system.remark": "Add Comment",
  "system.setCode": "Upgrade Runtime",
  "system.killStorage": "Clear Storage",

  // Contracts
  "contracts.instantiate": "Deploy Contract",
  "contracts.instantiateWithCode": "Deploy New Contract",
  "contracts.call": "Call Contract",
  "contracts.uploadCode": "Upload Contract Code",
  "contracts.removeCode": "Remove Contract Code",

  // DID
  "did.createDid": "Create Identity",
  "did.updateDid": "Update Identity",
  "did.deleteDid": "Delete Identity",

  // Checkpoint
  "checkpoint.createCheckpoint": "Create Checkpoint",
  "checkpoint.setCheckpointId": "Set Checkpoint",

  // Transaction Payment Events
  "transactionPayment.TransactionFeePaid": "Transaction Fee",

  // System Events
  "system.ExtrinsicSuccess": "Success",
  "system.ExtrinsicFailed": "Failed",

  // Default fallback
  default: "Transaction",
};

export const METHOD_DESCRIPTIONS = {
  "balances.transfer": "Transfer tokens from one account to another",
  "balances.transferKeepAlive":
    "Transfer tokens while ensuring the sender account remains active (won't go below existential deposit)",
  "balances.transferAll":
    "Transfer all available tokens from sender to receiver",
  "timestamp.set":
    "System operation to set the current block timestamp (called automatically)",
  "contracts.instantiate": "Deploy a smart contract using existing code",
  "contracts.call": "Execute a function on a smart contract",
  "did.createDid": "Create a new decentralized identity",
  "checkpoint.createCheckpoint":
    "Create a new checkpoint for rollback protection",
};

export const ERROR_MESSAGES = {
  // Token errors
  "Token.FundsUnavailable":
    "The account does not have enough available balance",
  "Token.BelowMinimum":
    "The transfer would result in a balance below the minimum allowed",
  "Token.CannotCreate": "Cannot create a new account with the provided balance",
  "Token.UnknownAsset": "The asset does not exist",
  "Token.Frozen": "The account or asset is frozen",

  // Module errors
  "Balances.InsufficientBalance":
    "The account does not have enough balance for this transaction",
  "Balances.ExistentialDeposit":
    "The resulting balance would be below the existential deposit",
  "Balances.KeepAlive":
    "The transfer would kill the sender account, but KeepAlive was specified",
  "Balances.TooManyReserves": "The account has too many reserved balance types",

  // Contract errors
  "Contracts.CodeNotFound": "The specified contract code does not exist",
  "Contracts.ContractNotFound": "The contract address does not exist",
  "Contracts.OutOfGas": "The contract execution ran out of gas",
  "Contracts.TransferFailed": "Token transfer to/from contract failed",

  // System errors
  "System.CallFiltered": "The call is not allowed in the current context",
  "System.FailedToExtractRuntimeVersion": "Failed to extract runtime version",

  // Default
  Unknown: "An unknown error occurred",
};

/**
 * Get user-friendly display name for a method
 */
export function getUserFriendlyMethodName(section, method) {
  const key = `${section}.${method}`;
  return METHOD_DISPLAY_NAMES[key] || `${section}.${method}`;
}

/**
 * Get description for a method
 */
export function getMethodDescription(section, method) {
  const key = `${section}.${method}`;
  return METHOD_DESCRIPTIONS[key] || "";
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error) {
  if (!error) return "Unknown error";

  // Handle Module errors (pallet-specific errors)
  if (error.type === "Module" && error.detail) {
    const key = `${error.detail.section}.${error.detail.method}`;
    const message =
      ERROR_MESSAGES[key] || error.detail.method || "Module Error";
    return message;
  }

  // Handle Token errors
  if (error.type === "Token" && error.detail) {
    const errorType = error.detail.type || error.detail;
    const key = `Token.${errorType}`;
    return ERROR_MESSAGES[key] || errorType || "Token Error";
  }

  // Handle Arithmetic errors
  if (error.type === "Arithmetic" && error.detail) {
    const errorType = error.detail.type || error.detail;
    return `Arithmetic Error: ${errorType}`;
  }

  // Handle Transactional errors
  if (error.type === "Transactional" && error.detail) {
    const errorType = error.detail.type || error.detail;
    return `Transactional Error: ${errorType}`;
  }

  // Fallback: return whatever we have
  if (error.type) return error.type;
  if (error.detail && typeof error.detail === "string") return error.detail;
  if (error.detail && error.detail.method) return error.detail.method;
  if (error.detail && error.detail.type) return error.detail.type;

  return "Transaction Error";
}

/**
 * Get detailed error description
 */
export function getErrorDescription(error) {
  if (!error) return "";

  // Try to get description from docs
  if (error.detail && error.detail.docs) {
    const docs = Array.isArray(error.detail.docs)
      ? error.detail.docs.join(" ")
      : error.detail.docs;
    if (docs && docs.trim()) return docs;
  }

  // Try to get from ERROR_MESSAGES
  if (error.type === "Module" && error.detail) {
    const key = `${error.detail.section}.${error.detail.method}`;
    const message = ERROR_MESSAGES[key];
    if (message) return message;
  }

  if (error.type === "Token" && error.detail) {
    const errorType = error.detail.type || error.detail;
    const key = `Token.${errorType}`;
    const message = ERROR_MESSAGES[key];
    if (message) return message;
  }

  return "";
}

/**
 * Format transaction parameters for display
 */
export function formatTransactionParams(extrinsic) {
  const { section, method, args } = extrinsic.call || {};

  if (
    section === "balances" &&
    ["transfer", "transferKeepAlive", "transferAll"].includes(method)
  ) {
    return {
      from: extrinsic.signer,
      to: args?.dest?.id || args?.dest,
      amount: args?.value || args?.amount,
      type: "transfer",
    };
  }

  if (section === "contracts" && method === "call") {
    return {
      from: extrinsic.signer,
      to: args?.dest?.id || args?.dest,
      value: args?.value,
      gasLimit: args?.gasLimit,
      type: "contract_call",
    };
  }

  return { type: "other", args };
}
